import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import AccessCustomizableOptions from './AccessCustomizableOptions';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import ExpandOptionsButton from './ExpandOptionsButton';
import OfferCard from './OfferCard/OfferCard';
import LoadingWrapper from './LoadingWrapper';
import { getQuoteFormalPricing } from 'api/accessexpress/api';
import {
  LocationType,
  ProductLocationType,
  ProductOfferingType,
  ProductType,
} from 'screens/SelectServices/schemas';
import ProductSummary from '../BaseComponents/ProductSummary';
import UnavailableServiceBanner from './UnavailableServiceBanner';
import { getSortedOfferings } from '../utils';
import { TermType } from 'screens/SelectServices/utils';
import { useCartContext } from '../CartProvider';
import { observer } from 'mobx-react-lite';
import CurrentLocationHeader from './CurrentLocationHeader';
import { SelectedPackages } from 'context/SingleServiceManagerContext';

const OFFER_TAG_INDEX_MAP: Record<string, number> = {
  'Best offer': 0,
  'Premium choice': 1,
  'Lowest price': 2,
};

const AccessProductDetails = observer(() => {
  const { category } = useParams();
  const {
    locations,
    goPrevLocation,
    goNextLocation,
    activeLocation,
    locationActiveId,
    loadingDraftForPricing,
    addAccessCartItem,
    removeAccessCartItem,
    isItemAlreadyInCart,
    quoteId,
  } = useCartContext();

  const { data, isLoading } = useQuery(
    ['access_products', quoteId],
    () => getQuoteFormalPricing(quoteId!),
    {
      enabled: !!quoteId && !!locations.length && !loadingDraftForPricing,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [expand, setExpand] = useState(false);
  const [offerCardsHeight, setOfferCardsHeight] = useState(0);

  const offerCardsRef = useRef<HTMLDivElement>(null);

  const currentAddress = activeLocation;
  const onExpand = () => setExpand((prev) => !prev);

  const calculateOfferCardsHeight = () => {
    if (offerCardsRef.current) {
      setOfferCardsHeight(offerCardsRef.current.clientHeight);
    }
  };

  useEffect(() => {
    calculateOfferCardsHeight();

    window.addEventListener('resize', calculateOfferCardsHeight);
    return () => {
      window.removeEventListener('resize', calculateOfferCardsHeight);
    };
  }, [data, expand]);

  const currentLocation: ProductLocationType = data?.locations?.find(
    (loc: LocationType & { external_id: string }) =>
      loc.external_id === currentAddress?.external_id,
  );

  const { bestOffer, premiumChoice, lowestPrice, sortedOfferings } =
    getSortedOfferings(currentLocation);

  const handleAddToCart = async (tag?: string, mns?: SelectedPackages) => {
    const index = OFFER_TAG_INDEX_MAP[tag ?? ''];
    const item = sortedOfferings?.[index];

    const offeringID = (item?.formal_broadband_product?.id ||
      item?.formal_dia_product?.id) as string;

    if (item) {
      if (isItemAlreadyInCart(offeringID)) {
        removeAccessCartItem(offeringID);
        return;
      }
      const { success = false }: { success: boolean } = await addAccessCartItem(
        { ...item, mns_essential: mns?.essential, mns_enhanced: mns?.enhanced },
      );
      if (success) {
        setSelectedOffer(tag ?? '');
      }
    }
  };

  const showExpandButton = bestOffer || premiumChoice || lowestPrice;

  const noCuratedOptions =
    currentLocation?.formal_products_offerings.length === 0;

  const serviceSummaryData = (offering?: ProductOfferingType) => {
    return {
      ...(offering?.formal_broadband_product || offering?.formal_dia_product),
      term: offering?.term,
    } as ProductType & { term: TermType };
  };

  const goToNextLocation = () => {
    setExpand(false);
    goNextLocation();
  };

  const goToPrevLocation = () => {
    setExpand(false);
    goPrevLocation();
  };

  return (
    <div className="relative my-0 flex min-h-screen w-full flex-col justify-between gap-8 p-4 sm:p-8">
      <ProductSummary
        product={{
          type: 'access',
          title: category as string,
        }}
        isAccessProduct
        isLoading={isLoading || loadingDraftForPricing}
      />
      {(noCuratedOptions || !data) && !isLoading && !loadingDraftForPricing && (
        <UnavailableServiceBanner
          id={activeLocation?.id as string}
          category={category as string}
        />
      )}
      <div className="relative flex w-full flex-1 flex-col gap-6 overflow-visible">
        {activeLocation && (
          <CurrentLocationHeader
            activeLocation={activeLocation}
            type={category as string}
          />
        )}
        <LoadingWrapper
          loading={isLoading || loadingDraftForPricing}
          show={true}
        >
          <div className={clsx('w-full scrollbar-none')}>
            <motion.div
              initial={{ opacity: 1, height: 'auto' }}
              animate={{ opacity: expand ? 0 : 1, height: expand ? 0 : 'auto' }}
              transition={{
                height: { duration: 0.7, ease: 'easeInOut' },
                opacity: { duration: 0.4, ease: 'easeInOut' },
              }}
              className="w-full"
            >
              <AnimatePresence>
                {!expand && (
                  <motion.div
                    ref={offerCardsRef}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: expand ? 0 : 1 }}
                    transition={{ opacity: { duration: 1.7 } }}
                    className="grid w-full grid-cols-1 md:grid-cols-2"
                  >
                    <div className="md:col-span-2">
                      <OfferCard
                        tag="Best offer"
                        offering={bestOffer}
                        isLoading={isLoading || loadingDraftForPricing}
                        selectedOffer={selectedOffer}
                        onAddToCart={handleAddToCart}
                        serviceSummaryData={serviceSummaryData(
                          sortedOfferings?.[0],
                        )}
                      />
                    </div>
                    <OfferCard
                      tag="Premium choice"
                      offering={premiumChoice}
                      isLoading={isLoading || loadingDraftForPricing}
                      selectedOffer={selectedOffer}
                      onAddToCart={handleAddToCart}
                      serviceSummaryData={serviceSummaryData(
                        sortedOfferings?.[1],
                      )}
                    />
                    <OfferCard
                      tag="Lowest price"
                      offering={lowestPrice}
                      isLoading={isLoading || loadingDraftForPricing}
                      selectedOffer={selectedOffer}
                      onAddToCart={handleAddToCart}
                      serviceSummaryData={serviceSummaryData(
                        sortedOfferings?.[2],
                      )}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            <div className="relative">
              {showExpandButton && (
                <ExpandOptionsButton
                  onExpand={onExpand}
                  expand={expand}
                  expandHeight={offerCardsHeight}
                />
              )}
              <AnimatePresence>
                {expand && (
                  <motion.div
                    key="other-options-content"
                    initial={{ scaleY: 0, opacity: 0, y: 50, height: 0 }}
                    animate={{ scaleY: 1, opacity: 1, y: 0, height: 'auto' }}
                    exit={{
                      scaleY: 0,
                      opacity: 0,
                      y: 50,
                      height: 0,
                      transition: {
                        height: { duration: 0.7, ease: 'easeInOut' },
                        opacity: { duration: 0.5, ease: 'easeOut' },
                        scaleY: { duration: 0.3, ease: 'easeOut' },
                      },
                    }}
                    transition={{
                      height: { duration: 0.7, ease: 'easeInOut' },
                      opacity: { duration: 0.8, ease: 'easeInOut' },
                      scaleY: { duration: 0.5, ease: 'easeInOut' },
                    }}
                    className="h-full w-full"
                  >
                    <AccessCustomizableOptions
                      expand={expand}
                      data={{
                        id: currentLocation?.id,
                        address: currentLocation?.address1,
                        offerings: sortedOfferings!,
                        type: 'broadband',
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </LoadingWrapper>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <GraniteButton
          variant="secondary"
          size="medium"
          onClick={goToPrevLocation}
          disabled={
            locationActiveId === locations[0]?.id || locations?.length <= 1
          }
        >
          Previous location
        </GraniteButton>
        <GraniteButton
          variant="tertiary"
          size="medium"
          onClick={goToNextLocation}
          disabled={
            locationActiveId === locations[locations.length - 1]?.id ||
            locations?.length <= 1
          }
        >
          Next location
        </GraniteButton>
      </div>
    </div>
  );
});

export default AccessProductDetails;
